var moment = require('moment');
import store from '@/store.js'

export {
    deepClone,
    convertToPascalCase,
    convertToCamelCase,
    convertToBase64,
    formatDate,
    getReducedFraction,
    htmlToText,
    sanitizeHtml,
    escapeHtml,
    apiErrorField,
    genericValidator,
    errorValidator,
    isAdmin,
    isCoach
};

const ADMIN = "Admin";
const COACH = "Coach";

function deepClone(destination, source) {
    return Object.assign(destination, JSON.parse(JSON.stringify(source)));
}

function convertToPascalCase(string) {
    return string.replace(/(\w)(\w*)/g,
        function (g0, g1, g2) { return g1.toUpperCase() + g2.toLowerCase(); });
}

function convertToCamelCase(str) {
    return str.replace(/(?:^\w|[A-Z]|\b\w)/g, function (word, index) {
        return index === 0 ? word.toLowerCase() : word.toUpperCase();
    }).replace(/\s+/g, '');
}

// function to encode file data to base64 encoded string
function convertToBase64(blob) {
    if (!blob) {
        return Promise.resolve(false);
    }
    else {
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        return new Promise(resolve => {
            reader.onloadend = () => {
                resolve(reader.result);
            };
        });
    }
}

function formatDate(date) {
    if (!(date instanceof Date)) {
        date = new Date(date);
    }

    return moment(date).format('DD/MM/YYYY');
}

function getReducedFraction(numerator, denominator) {
    var gcd = getCommonDenominator(numerator, denominator);
    return `${numerator / gcd}:${denominator / gcd}`
}

function getCommonDenominator(a, b) {
    return b ? getCommonDenominator(b, a % b) : a;
};

function htmlToText(html) {
    // DOMParser documents are inert: unlike innerHTML on a live element, nothing loads and no handler runs
    const doc = new DOMParser().parseFromString(html.trim(), 'text/html');
    return doc.body.textContent || "";
}

// Rich text written by users (session description, chat messages) is loaded into the editor, which writes it to innerHTML.
// Keep only the formatting the editor (Quill) produces and drop everything else (scripts, event handlers, javascript: urls).
const SANITIZE_TAGS = ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'sub', 'sup', 'ul', 'ol', 'li', 'a', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'pre', 'code', 'span', 'div', 'img', 'iframe'];
const SANITIZE_DROP = ['script', 'style', 'template', 'noscript', 'noembed', 'noframes', 'textarea', 'xmp', 'title', 'object', 'embed', 'svg', 'math'];
const SANITIZE_STYLES = ['color', 'background-color', 'text-align'];

function sanitizeHtml(html) {
    if (html === null || html === undefined) return html;
    const source = new DOMParser().parseFromString(String(html), 'text/html');
    const target = document.implementation.createHTMLDocument('');
    copySanitizedChildren(source.body, target.body, target);
    return target.body.innerHTML;
}

function copySanitizedChildren(from, to, doc) {
    for (const node of from.childNodes) {
        if (node.nodeType === Node.TEXT_NODE) {
            to.appendChild(doc.createTextNode(node.nodeValue));
            continue;
        }
        if (node.nodeType !== Node.ELEMENT_NODE) continue;
        const tag = node.localName;
        if (SANITIZE_DROP.includes(tag)) continue;
        if (!SANITIZE_TAGS.includes(tag)) {
            // unknown tag: keep its text
            copySanitizedChildren(node, to, doc);
            continue;
        }
        const element = doc.createElement(tag);
        const href = node.getAttribute('href');
        const src = node.getAttribute('src');
        if (tag === 'a' && href && /^(https?:|mailto:)/i.test(href.trim())) {
            element.setAttribute('href', href.trim());
            element.setAttribute('target', '_blank');
            element.setAttribute('rel', 'noopener noreferrer');
        }
        if (tag === 'img') {
            if (!src || !/^(https:|data:image\/(png|jpe?g|gif|webp);)/i.test(src.trim())) continue;
            element.setAttribute('src', src.trim());
        }
        if (tag === 'iframe') {
            // Quill video embed
            if (!src || !/^https:/i.test(src.trim())) continue;
            element.setAttribute('src', src.trim());
            element.setAttribute('allowfullscreen', 'true');
            element.setAttribute('frameborder', '0');
        }
        const classes = (node.getAttribute('class') || '').split(/\s+/).filter(name => /^ql-[a-z0-9-]+$/.test(name));
        if (classes.length) element.setAttribute('class', classes.join(' '));
        const styles = Array.from(node.style || []).filter(name => SANITIZE_STYLES.includes(name) && /^[#a-z0-9(),.%\s-]+$/i.test(node.style.getPropertyValue(name)));
        if (styles.length) element.setAttribute('style', styles.map(name => `${name}: ${node.style.getPropertyValue(name)}`).join('; '));
        // img and iframe never carry children (iframe content is serialized unescaped)
        if (tag !== 'img' && tag !== 'iframe') copySanitizedChildren(node, element, doc);
        to.appendChild(element);
    }
}

function escapeHtml(text) {
    return String(text === null || text === undefined ? '' : text).replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]));
}

function genericValidator(val, attemptedSubmit) {
    var result = undefined; // This is the default, in case the user hasn't attemptedSubmit yet

    if (attemptedSubmit) {

        if(val instanceof Array) 
        {
            result = Boolean(val.length);
        }
        else if(val instanceof Object) // Multilanguage field 
        {
            // Validate all fields (true when all are filled, like a plain field: `genericValidator(x) && errorValidator(...)`
            // then still lets the API's error mark the field)
            result = true;
            for (const [key, value] of Object.entries(val)) {
                if(!value)
                {
                    result = false;
                    break;
                }
            }
        }
        else
        {
            result = Boolean(val);
        }    
    }

    return result;
}

function errorValidator(fieldName) {
    var result = undefined;
    const alertOutput = store.state.alert.output;

    if (alertOutput && Array.isArray(alertOutput)) // Error alertOutput
    {
        if (alertOutput.find(obj => {
            return obj.key === fieldName
        })) {
            result = false;
        }
    }

    return result;
}

function isAdmin(user)
{
    if(!user)
    {
        user = JSON.parse(localStorage.getItem('user'));
    }

    return user && user.data && user.data.role == ADMIN;
}

function isCoach(user)
{
    if(!user)
    {
        user = JSON.parse(localStorage.getItem('user'));
    }

    return user && user.data && user.data.role == COACH;
}

// The API keys most errors by the request's property name, but ASP.NET Identity errors arrive keyed by their
// code (DuplicateEmail, PasswordTooShort...), list items by their path (Values[0].Value) and a few by an id
// name. Map them to the field a form shows them on; a key that maps to no field is shown by FormApiErrors.
const API_ERROR_FIELDS = {
    DuplicateUserName: 'Email', DuplicateEmail: 'Email', InvalidEmail: 'Email', InvalidUserName: 'Email', Username: 'Email',
    PasswordTooShort: 'Password', PasswordRequiresDigit: 'Password', PasswordRequiresLower: 'Password', PasswordRequiresUpper: 'Password',
    PasswordRequiresNonAlphanumeric: 'Password', PasswordRequiresUniqueChars: 'Password', PasswordMismatch: 'Password',
    InvalidToken: 'Token',
    CharacterId: 'Character',
    MaxStudentsExceeded: 'Students',
};

function apiErrorField(key) {
    const base = String(key).split(/[[.]/)[0];
    return API_ERROR_FIELDS[base] || base;
}
